
import styled from "styled-components";
import { COLOR } from '../../../helpers/theme';
import { Room } from "../../../interfaces/Room";
import Spinner from '../../../components/Spinner';
import React, { useEffect, useState } from "react";
import Card from "../../../components/layouts/Card";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from '../../../redux/rootReducers';
import Button from '../../../components/forms/ButtonEx';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageWrapper from "../../../components/layouts/PageWrapper";
import { InputWrapper } from "../../../components/forms/FormComponents";
import { BootstrapGrid } from "../../../components/layouts/BootstrapGrid";
import { createRoomAsync, resetState, selectRoomAsync, setError, updateRoomAsync } from "../../../redux/reducers/rooms/rooms.actions";

const RoomsManagePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  
  const errors = useSelector((state: RootState) => state.room.error);
  const isLoading = useSelector((state: RootState) => state.room.isLoading);
  const createdRoom = useSelector((state: RootState) => state.room.createdRoom);
  const selectedRoom = useSelector((state: RootState) => state.room.selectedRoom);

  const [formTitle, setFormTitle] = useState<string>("Tambah");
  const [room, setRoom] = useState<Room>({
    room_name: '',
    room_building: '',
    room_floor: '',
    room_capacity: 0,
    room_featured_image: '',
  });

  const populateData = () => {
    const paths = location.pathname.split("/");
    if (paths[3] !== 'create') {
      const roomId = parseInt(paths[3]);
      if (roomId && !isNaN(roomId)) {
        dispatch(selectRoomAsync.request({ id: roomId }));
        setFormTitle('Ubah');
      } else {
        navigate(`/building-management/rooms/create`);
      }
    }
  }

  useEffect(() => {
    dispatch(resetState());
  }, [])

  useEffect(() => {
    populateData();
  }, [location.pathname]);


  useEffect(() => {
    if (selectedRoom) {
      setRoom(selectedRoom);
    } else {
      setRoom({
        room_name: '',
        room_building: '',
        room_floor: '',
        room_capacity: 0,
        room_featured_image: '',
      });
    }
  }, [selectedRoom]);

  const whichForm = () => {
    const paths = location.pathname.split("/");
    const roomId = paths[3];
    if (selectedRoom && roomId && !isNaN(parseInt(roomId))) {
      return 'existing_data';
    } else if (roomId === 'create') {
      return 'new_data';
    } else {
      return 'unknown';
    }
  }

  const refreshPage = () => {
    if (whichForm() === 'existing_data') {
      populateData();   
    }
  }
 
  const clearError = (fieldName: string) => {
    if (errors) {
      dispatch(setError({
        ...errors,
        errors: {
          ...errors.errors,
          [`${fieldName}`]: []
        }
      }));
    }
  }

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearError('room_name');
    setRoom({
      ...room,
      room_name: e.target.value
    });
  };

  const onChangeBuilding = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearError('room_building');
    setRoom({
      ...room,
      room_building: e.target.value
    });
  };

  const onChangeFloor = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearError('room_floor');
    setRoom({
      ...room,
      room_floor: e.target.value
    });
  };

  const onChangeCapacity = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value);
    if (!val || isNaN(val)) return;

    clearError('room_floor');
    setRoom({
      ...room,
      room_capacity: val
    });
  };

  const onChangeFeaturedImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearError('room_floor');
    setRoom({
      ...room,
      room_featured_image: e.target.value
    });
  };

  const actionCreate = () => {
    const paths = location.pathname.split("/");
    const roomId = paths[3];
    if (selectedRoom && roomId && !isNaN(parseInt(roomId))) {
      dispatch(updateRoomAsync.request(room));
    } else if (roomId === 'create') {
      dispatch(createRoomAsync.request(room));
    }
  }

  const fieldHasErrors = (fieldName: string) => {
    if (errors && errors.errors && errors.errors[fieldName] && errors.errors[fieldName].length > 0) {
      return true;
    }
    return false;
  }

  const fieldErrorMessage = (fieldName: string) => {
    if (errors && errors.errors && errors.errors[fieldName]) {
      const fieldErrors = errors.errors[fieldName];
      if (fieldErrors.length === 1) {
        return fieldErrors[0];
      } else {
        return (
          <ul>
            {fieldErrors.map(item => {
              return <li>{item}</li>
            })}
          </ul>
        );
      }
    }
    return "";
  }

  const renderFormSuccess = () => {
    const labelFinished = whichForm() === 'new_data' ? 'ditambahkan' : 'diubah';
    return (
      <React.Fragment>
        <div className="formSuccess">
          <h3>Ruangan <b>{ createdRoom?.room_name }</b> berhasil { labelFinished }!</h3>
        </div>
        <div className="formSubmit">
          <Link to='/building-management/rooms'>
            <Button caption="Kembali ke Daftar Ruangan" />
          </Link>
          <p>atau <Link to={`/building-management/rooms/${createdRoom?.id}`} onClick={refreshPage} className="formSubmit--update">ubah data ruangan</Link></p>
        </div>
      </React.Fragment>
    );
  }

  const renderFormLoading = () => {
    return (
      <React.Fragment>
        <div className="formLoading">
          <Spinner size="small" />
          <div className="formLoading--caption">Memuat Data...</div>
        </div>
      </React.Fragment>
    )
  }

  const renderForm = () => {
    return (
      <React.Fragment>
        <div className="form-group">
          <InputWrapper hasErrors={fieldHasErrors('room_name')}>
            <div className="input--field">
              <label>Nama Ruangan</label>
              <input
                type="text"
                disabled={isLoading}
                value={room.room_name}
                onChange={onChangeName}
                placeholder="Masukkan nama ruangan disini"
              />
            </div>
            <div className="input--errors">
              { fieldErrorMessage('room_name') }
            </div>
          </InputWrapper>
        </div>
        <div className="form-group">
          <InputWrapper hasErrors={fieldHasErrors('room_building')}>
            <div className="input--field">
              <label>Jumlah</label>
              <input
                type="number"
                disabled={isLoading}
                value={room.room_building}
                onChange={onChangeBuilding}
                placeholder="Masukkan gedung ruangan disini"
              />
            </div>
            <div className="input--errors">
              { fieldErrorMessage('room_building') }
            </div>
          </InputWrapper>
        </div>
        <div className="form-group">
          <InputWrapper hasErrors={fieldHasErrors('room_floor')}>
            <div className="input--field">
              <label>Ikon</label>
              <input
                type="text"
                disabled={isLoading}
                value={room.room_floor}
                onChange={onChangeFloor}
                placeholder="Pilih ikon ruangan"
              />
            </div>
            <div className="input--errors">
              { fieldErrorMessage('room_floor') }
            </div>
          </InputWrapper>
        </div>
        <div className="form-group">
          <InputWrapper hasErrors={fieldHasErrors('room_capacity')}>
            <div className="input--field">
              <label>Kapasitas Ruangan</label>
              <input
                type="number"
                disabled={isLoading}
                value={room.room_capacity}
                onChange={onChangeCapacity}
                placeholder="Masukkan kapasitas ruangan"
              />
            </div>
            <div className="input--errors">
              { fieldErrorMessage('room_capacity') }
            </div>
          </InputWrapper>
        </div>
        <div className="form-group">
          <InputWrapper hasErrors={fieldHasErrors('room_featured_image')}>
            <div className="input--field">
              <label>Gambar Barang</label>
              <input
                type="text"
                disabled={isLoading}
                value={room.room_featured_image}
                onChange={onChangeFeaturedImage}
                placeholder="Unggah gambar ruangan"
              />
            </div>
            <div className="input--errors">
              { fieldErrorMessage('room_featured_image') }
            </div>
          </InputWrapper>
        </div>

        <div className="formSubmit">
          <Button
            disabled={isLoading}
            isLoading={isLoading}
            onClick={actionCreate}
            caption={`${formTitle} Ruangan`}
          />
        </div>
      </React.Fragment>
    );
  }

  return (
    <PageWrapper>
      <RoomsManagePageWrapper>
        <BootstrapGrid>
          <div className="row">
            <div className="col-lg-4">
              <Card>
                <div className="card--title">
                  <h2 className="card--title-title">{formTitle} Ruangan</h2>
                </div>
                <div className="card--body">
                  { isLoading ? renderFormLoading() : createdRoom ? renderFormSuccess() : renderForm() }
                </div>
              </Card>
              
            </div>
          </div>
        </BootstrapGrid>
      </RoomsManagePageWrapper>
    </PageWrapper>
  );
}

const RoomsManagePageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;

  & .formTitle {
    margin-bottom: 10px;
  }

  & .formLoading {
    display: flex;
    align-items: center;
    justify-content: center;
    & > .formLoading--caption {
      font-size: 14px;
      margin-top: -3px;
      margin-left: 10px;
    }
  }

  & .formSuccess {
    & > h3 {
      font-size: 14px;
      font-weight: normal;
      text-align: center;
    }
  }
  
  & .formSubmit {
    margin-top: 30px;
    text-align: center;

    & > p {
      margin-top: 5px;
      & > .formSubmit--update {
        cursor: pointer;
        color: ${COLOR.SECONDARY}
      }
    }
  }
`;

export default RoomsManagePage;